'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function PresentValueCalculator() {
  const t = useTranslations("tools.presentValueCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Future Lump Sum inputs
  const [futureValue, setFutureValue] = useState(1000);
  const [periods, setPeriods] = useState(10);
  const [interestRate, setInterestRate] = useState(6);

  // Annuity inputs
  const [periodsAnnuity, setPeriodsAnnuity] = useState(10);
  const [interestRateAnnuity, setInterestRateAnnuity] = useState(6);
  const [payment, setPayment] = useState(100);
  const [paymentTiming, setPaymentTiming] = useState('end');

  // Results
  const [futureResults, setFutureResults] = useState<any>(null);
  const [annuityResults, setAnnuityResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const formatCurrency = (value: number) => {
    return '$' + Math.abs(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const formatPercent = (value: number) => {
    return value.toFixed(2) + '%';
  };

  const handleFutureCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const fv = futureValue;
    const n = periods;
    const rate = interestRate / 100;

    const pv = fv / Math.pow(1 + rate, n);
    const interest = fv - pv;

    setFutureResults({
      pv,
      fv,
      interest,
      periods: n,
      rate: interestRate
    });

    setAnnuityResults(null);
  };

  const handleAnnuityCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const n = periodsAnnuity;
    const rate = interestRateAnnuity / 100;
    const pmt = payment;
    const timing = paymentTiming;

    let pv, fv;

    if (timing === 'end') {
      pv = pmt * ((1 - Math.pow(1 + rate, -n)) / rate);
      fv = pmt * ((Math.pow(1 + rate, n) - 1) / rate);
    } else {
      pv = pmt * ((1 - Math.pow(1 + rate, -n)) / rate) * (1 + rate);
      fv = pmt * ((Math.pow(1 + rate, n) - 1) / rate) * (1 + rate);
    }

    const totalPrincipal = pmt * n;
    const totalInterest = fv - totalPrincipal;

    // Generate schedule
    const schedule = [];
    let balance = 0;

    for (let i = 1; i <= Math.min(n, 20); i++) {
      let interest, principal;

      if (timing === 'beginning') {
        balance += pmt;
        interest = balance * rate;
        balance += interest;
        principal = pmt;
      } else {
        interest = balance * rate;
        balance += interest;
        balance += pmt;
        principal = pmt;
      }

      schedule.push({
        period: i,
        payment: pmt,
        interest,
        principal,
        balance
      });
    }

    setAnnuityResults({
      pv,
      fv,
      totalPrincipal,
      totalInterest,
      schedule,
      periods: n,
      rate: interestRateAnnuity,
      timing
    });

    setFutureResults(null);
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.08)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '900px' },
    { maxWidth: '1100px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const calculatorGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr', gap: '2rem' },
    { gridTemplateColumns: '1fr 1fr', gap: '2rem' }
  );

  const calculatorCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    { marginBottom: '1rem' },
    {},
    { marginBottom: '1.25rem' },
    { marginBottom: '1.5rem' }
  );

  const labelStyle = getResponsiveStyle(
    { display: 'block', marginBottom: '0.5rem', color: '#475569', fontWeight: 500 },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem 1rem', fontSize: '1rem' },
    { padding: '0.875rem 1.25rem', fontSize: '1.125rem' },
    { padding: '1rem 1.5rem', fontSize: '1.125rem' }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '0.5rem'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const resultHighlightStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      color: 'white',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
      textAlign: 'center' as const
    },
    { padding: '1.25rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '1.5rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', marginBottom: '2rem' },
    { padding: '2.5rem', marginBottom: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.125rem', lineHeight: 1.8 }
  );

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>{t("toolTitle")}</h2>
            <p style={summaryTextStyle}>{t("toolSubtitle")}</p>
          </div>

          <div style={calculatorGridStyle}>
            {/* Future Lump Sum Calculator */}
            <div style={calculatorCardStyle}>
              <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginBottom: '1.5rem' }}>
                {t("futureLumpSumTitle")}
              </h3>
              <form onSubmit={handleFutureCalculate}>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="future-value">{t("futureValueLabel")}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="future-value"
                    step="0.01"
                    value={futureValue}
                    onChange={(e) => setFutureValue(parseFloat(e.target.value))}
                    required
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="periods">{t("periodsLabel")}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="periods"
                    step="1"
                    value={periods}
                    onChange={(e) => setPeriods(parseFloat(e.target.value))}
                    required
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="interest-rate">{t("interestRateLabel")}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="interest-rate"
                    step="0.01"
                    value={interestRate}
                    onChange={(e) => setInterestRate(parseFloat(e.target.value))}
                    required
                  />
                </div>
                <button type="submit" style={calculateButtonStyle}>
                  {t("calculateButton")}
                </button>
              </form>
            </div>

            {/* Annuity Calculator */}
            <div style={calculatorCardStyle}>
              <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginBottom: '1.5rem' }}>
                {t("periodicPaymentsTitle")}
              </h3>
              <form onSubmit={handleAnnuityCalculate}>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="periods-annuity">{t("periodsLabel")}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="periods-annuity"
                    step="1"
                    value={periodsAnnuity}
                    onChange={(e) => setPeriodsAnnuity(parseFloat(e.target.value))}
                    required
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="interest-rate-annuity">{t("interestRateLabel")}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="interest-rate-annuity"
                    step="0.01"
                    value={interestRateAnnuity}
                    onChange={(e) => setInterestRateAnnuity(parseFloat(e.target.value))}
                    required
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="payment">{t("paymentLabel")}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="payment"
                    step="0.01"
                    value={payment}
                    onChange={(e) => setPayment(parseFloat(e.target.value))}
                    required
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("paymentTimingLabel")}</label>
                  <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="payment-timing"
                        value="end"
                        checked={paymentTiming === 'end'}
                        onChange={(e) => setPaymentTiming(e.target.value)}
                        style={{ cursor: 'pointer' }}
                      />
                      <span>{t("endOfPeriod")}</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="payment-timing"
                        value="beginning"
                        checked={paymentTiming === 'beginning'}
                        onChange={(e) => setPaymentTiming(e.target.value)}
                        style={{ cursor: 'pointer' }}
                      />
                      <span>{t("beginningOfPeriod")}</span>
                    </label>
                  </div>
                </div>
                <button type="submit" style={calculateButtonStyle}>
                  {t("calculateButton")}
                </button>
              </form>
            </div>
          </div>

          {/* Future Results */}
          {futureResults && (
            <div style={resultsStyle}>
              <h3 style={h2Style}>{t("futureLumpSumResults")}</h3>
              <div style={resultHighlightStyle}>
                <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>
                  {t("presentValue")}
                </div>
                <div style={{ fontSize: screenSize === 'mobile' ? '2rem' : '2.5rem', fontWeight: 700 }}>
                  {formatCurrency(futureResults.pv)}
                </div>
              </div>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.875rem 1rem',
                  background: '#f8fafc',
                  borderRadius: '0.5rem',
                  borderLeft: '4px solid #2563eb'
                }}>
                  <span style={{ color: '#64748b', fontWeight: 500 }}>{t("futureValue")}</span>
                  <span style={{ color: colors.textPrimary, fontWeight: 600 }}>{formatCurrency(futureResults.fv)}</span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.875rem 1rem',
                  background: '#f8fafc',
                  borderRadius: '0.5rem',
                  borderLeft: '4px solid #2563eb'
                }}>
                  <span style={{ color: '#64748b', fontWeight: 500 }}>{t("totalInterest")}</span>
                  <span style={{ color: colors.textPrimary, fontWeight: 600 }}>{formatCurrency(futureResults.interest)}</span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.875rem 1rem',
                  background: '#f8fafc',
                  borderRadius: '0.5rem',
                  borderLeft: '4px solid #2563eb'
                }}>
                  <span style={{ color: '#64748b', fontWeight: 500 }}>{t("numberOfPeriods")}</span>
                  <span style={{ color: colors.textPrimary, fontWeight: 600 }}>{futureResults.periods}</span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.875rem 1rem',
                  background: '#f8fafc',
                  borderRadius: '0.5rem',
                  borderLeft: '4px solid #2563eb'
                }}>
                  <span style={{ color: '#64748b', fontWeight: 500 }}>{t("interestRate")}</span>
                  <span style={{ color: colors.textPrimary, fontWeight: 600 }}>{formatPercent(futureResults.rate)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Annuity Results */}
          {annuityResults && (
            <div style={resultsStyle}>
              <h3 style={h2Style}>{t("periodicPaymentsResults")}</h3>
              <div style={resultHighlightStyle}>
                <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>
                  {t("presentValue")}
                </div>
                <div style={{ fontSize: screenSize === 'mobile' ? '2rem' : '2.5rem', fontWeight: 700 }}>
                  {formatCurrency(annuityResults.pv)}
                </div>
              </div>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.875rem 1rem',
                  background: '#f8fafc',
                  borderRadius: '0.5rem',
                  borderLeft: '4px solid #2563eb'
                }}>
                  <span style={{ color: '#64748b', fontWeight: 500 }}>{t("futureValue")}</span>
                  <span style={{ color: colors.textPrimary, fontWeight: 600 }}>{formatCurrency(annuityResults.fv)}</span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.875rem 1rem',
                  background: '#f8fafc',
                  borderRadius: '0.5rem',
                  borderLeft: '4px solid #2563eb'
                }}>
                  <span style={{ color: '#64748b', fontWeight: 500 }}>{t("totalPrincipal")}</span>
                  <span style={{ color: colors.textPrimary, fontWeight: 600 }}>{formatCurrency(annuityResults.totalPrincipal)}</span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.875rem 1rem',
                  background: '#f8fafc',
                  borderRadius: '0.5rem',
                  borderLeft: '4px solid #2563eb'
                }}>
                  <span style={{ color: '#64748b', fontWeight: 500 }}>{t("totalInterest")}</span>
                  <span style={{ color: colors.textPrimary, fontWeight: 600 }}>{formatCurrency(annuityResults.totalInterest)}</span>
                </div>
              </div>

              {/* Schedule Table */}
              <div style={{ marginTop: '1.5rem', overflowX: 'auto' }}>
                <h4 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '1rem' }}>
                  {t("accumulationSchedule")}
                </h4>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', color: '#475569', fontWeight: 600, fontSize: '0.875rem' }}>
                        {t("period")}
                      </th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', color: '#475569', fontWeight: 600, fontSize: '0.875rem' }}>
                        {t("payment")}
                      </th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', color: '#475569', fontWeight: 600, fontSize: '0.875rem' }}>
                        {t("interest")}
                      </th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', color: '#475569', fontWeight: 600, fontSize: '0.875rem' }}>
                        {t("principal")}
                      </th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', color: '#475569', fontWeight: 600, fontSize: '0.875rem' }}>
                        {t("balance")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {annuityResults.schedule.map((row: any) => (
                      <tr key={row.period} style={{ transition: 'background 0.2s' }}>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0', color: colors.textPrimary, fontSize: '0.875rem' }}>
                          {row.period}
                        </td>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0', color: colors.textPrimary, fontSize: '0.875rem' }}>
                          {formatCurrency(row.payment)}
                        </td>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0', color: colors.textPrimary, fontSize: '0.875rem' }}>
                          {formatCurrency(row.interest)}
                        </td>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0', color: colors.textPrimary, fontSize: '0.875rem' }}>
                          {formatCurrency(row.principal)}
                        </td>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0', color: colors.textPrimary, fontSize: '0.875rem' }}>
                          {formatCurrency(row.balance)}
                        </td>
                      </tr>
                    ))}
                    {annuityResults.periods > 20 && (
                      <tr>
                        <td colSpan={5} style={{ padding: '0.75rem', textAlign: 'center', color: '#64748b', fontStyle: 'italic', fontSize: '0.875rem' }}>
                          {t("showingFirstPeriods", { count: 20, total: annuityResults.periods })}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={{ ...paragraphStyle, paddingLeft: '1.5rem' }}>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep1")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep2")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep3")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep4")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep5")}</li>
        </ol>
      </section>

      {/* Key Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <div style={{ display: 'grid', gap: '1rem' }}>
          <div style={{ padding: '1rem', background: 'rgba(37, 99, 235, 0.05)', borderRadius: '0.5rem', borderLeft: '4px solid #2563eb' }}>
            <h3 style={{ fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' }}>
              {t("insightTimeValueTitle")}
            </h3>
            <p style={{ ...paragraphStyle, marginBottom: 0 }}>{t("insightTimeValueText")}</p>
          </div>
          <div style={{ padding: '1rem', background: 'rgba(16, 185, 129, 0.05)', borderRadius: '0.5rem', borderLeft: '4px solid #10b981' }}>
            <h3 style={{ fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' }}>
              {t("insightDiscountRateTitle")}
            </h3>
            <p style={{ ...paragraphStyle, marginBottom: 0 }}>{t("insightDiscountRateText")}</p>
          </div>
          <div style={{ padding: '1rem', background: 'rgba(245, 158, 11, 0.05)', borderRadius: '0.5rem', borderLeft: '4px solid #f59e0b' }}>
            <h3 style={{ fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' }}>
              {t("insightApplicationsTitle")}
            </h3>
            <p style={{ ...paragraphStyle, marginBottom: 0 }}>{t("insightApplicationsText")}</p>
          </div>
        </div>
      </section>

      {/* Understanding Present Value */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("understandingTitle")}</h2>
        <h3 style={{ fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem' }}>
          {t("formulaTitle")}
        </h3>
        <p style={paragraphStyle}>{t("formulaText")}</p>
        <div style={{ padding: '1rem', background: '#f8fafc', borderRadius: '0.5rem', marginBottom: '1rem', fontFamily: 'monospace', fontSize: screenSize === 'mobile' ? '0.875rem' : '1rem' }}>
          PV = FV / (1 + r)^n
        </div>
        <p style={paragraphStyle}>{t("formulaExplanation")}</p>

        <h3 style={{ fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem', marginTop: '1.5rem' }}>
          {t("annuityFormulaTitle")}
        </h3>
        <p style={paragraphStyle}>{t("annuityFormulaText")}</p>
        <div style={{ padding: '1rem', background: '#f8fafc', borderRadius: '0.5rem', marginBottom: '1rem', fontFamily: 'monospace', fontSize: screenSize === 'mobile' ? '0.875rem' : '1rem' }}>
          PV = PMT × [(1 - (1 + r)^-n) / r]
        </div>
      </section>

      {/* Best Practices */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("bestPracticesTitle")}</h2>
        <ul style={{ ...paragraphStyle, paddingLeft: '1.5rem' }}>
          <li style={{ marginBottom: '0.75rem' }}>{t("bestPractice1")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("bestPractice2")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("bestPractice3")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("bestPractice4")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("bestPractice5")}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          <div>
            <h3 style={{ fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' }}>
              {t("faq1Question")}
            </h3>
            <p style={{ ...paragraphStyle, marginBottom: 0 }}>{t("faq1Answer")}</p>
          </div>
          <div>
            <h3 style={{ fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' }}>
              {t("faq2Question")}
            </h3>
            <p style={{ ...paragraphStyle, marginBottom: 0 }}>{t("faq2Answer")}</p>
          </div>
          <div>
            <h3 style={{ fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' }}>
              {t("faq3Question")}
            </h3>
            <p style={{ ...paragraphStyle, marginBottom: 0 }}>{t("faq3Answer")}</p>
          </div>
          <div>
            <h3 style={{ fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' }}>
              {t("faq4Question")}
            </h3>
            <p style={{ ...paragraphStyle, marginBottom: 0 }}>{t("faq4Answer")}</p>
          </div>
          <div>
            <h3 style={{ fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' }}>
              {t("faq5Question")}
            </h3>
            <p style={{ ...paragraphStyle, marginBottom: 0 }}>{t("faq5Answer")}</p>
          </div>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ul style={{ ...paragraphStyle, paddingLeft: '1.5rem', listStyle: 'none' }}>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.financialmentor.com/calculator/present-value-calculator" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {t("reference1")}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.wallstreetprep.com/knowledge/present-value-pv/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {t("reference2")}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.studyfinance.com/present-value/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {t("reference3")}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.calculatorsoup.com/calculators/financial/present-value-calculator-basic.php" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {t("reference4")}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://breakingintowallstreet.com/kb/finance/present-value/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {t("reference5")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}